import * as Sentry from '@sentry/browser';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import smartlookClient from 'smartlook-client';

import { createAction } from 'redux-actions';

import * as Api from '../api/api';
import { UserApiClient } from '../api/user-api-client';
import { normalizeUserAttributes } from '../product-telemetry/product-telemetry-controller';
import { RealTime } from '../real-time/real-time';

import { isProd } from '../config/config';
import { CelloController } from '../util/Cello';
import { HelpScout } from '../util/helpscout-beacon';
import { KlaviyoTracking } from '../util/klaviyo-tracking';
import { UserflowController } from '../util/userflow-controller';

export const registerUserAction = createAction('REGISTER_USER');

// The key that Smartlook uses to ensure that our Javascript client is the
// one reporting to them (since we are bound by usage there). You can find this
// here: https://app.smartlook.com/org/2d40f605e151e8ebbdd6572a/settings/projects/0e8972f85fd6d90c5ff522e8
const SMARTLOOK_PROJECT_KEY = '44b9fd6649502cc897e671649a3000cfa206b06c';

/**
 * Handle putting the user in our user store. This runs after the user provider
 * knows that we have a user. In the transition, the api.ts is handling using
 * either the Auth0 or Cognito credentials, so the rest of our app (including this)
 * won't care.
 */
export const registerUser = async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  const response = await Api.get('/current-user');
  if (!response.ok) {
    throw new Error('/current-user request failed');
  }
  const u: AuthenticatedUser = await response.json();
  const normalizedUserAttributes = normalizeUserAttributes(u);

  // Sentry's set user cares about id (which is created by sentry by default),
  // username, and email specifically
  // The rest of the attributes get stored as key/value pairs and is shown
  // on their dashboard
  // https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
  Sentry.setUser(normalizedUserAttributes);

  // We don't care about capturing screen records of users developing, so wrap this
  // initialization in a `isProd` check where the others we don't
  if (isProd) {
    if (!(window as any).smartlook) {
      smartlookClient.init(SMARTLOOK_PROJECT_KEY);
    }
    // Similar to Sentry above, we can pass arbitrary keys to Smartlook and they'll
    // be able to be used in the dashboard
    // https://web.developer.smartlook.com/docs/identify-visitor#what-can-be-displayed-in-the-dashboard
    smartlookClient.identify(u.username, normalizedUserAttributes);
  }

  // Klaviyo has a much smaller set of supported properties, listed here
  // https://developers.klaviyo.com/en/v1-2/docs/identify-api-reference#specific-properties
  // So we just pass the name and email
  KlaviyoTracking.employer.identifyUser(u.email, u.firstName, u.lastName);

  // Helpscout does have arbitrary key value pairs, but they have to be whitelisted in the app first
  // An overview of the process exists here: https://developer.helpscout.com/beacon-2/web/javascript-api/#beaconidentify-userobject
  // But generally, go to this page: https://secure.helpscout.net/settings/properties/ and add your new property
  // Then add the values in this method
  // You'll also need to bulk import the values you intend to use operationally if you don't want to wait around for people
  // to log in
  HelpScout.beacon.identifyUser(u.email, normalizedUserAttributes);

  // Use Pusher to subscribe this user. Note that unlike the other initializations, this one is
  // largely controlled by us and is a bit more complicated
  RealTime.init(u);

  dispatch(registerUserAction(u));

  // eslint-disable-next-line no-console
  UserApiClient.logAccess().catch((e) => console.error(e));
  CelloController.init(u.email, u.firstName, u.lastName);

  await UserflowController.init(u);
};
